import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {PortalApi, WebmailApi} from "components/src/api/portal/generated";
import {Attachments} from "components/src/common/Attachments";
import {ConfirmDialog} from "components/src/common/ConfirmDialog";
import {DialogCloseCause} from "components/src/common/Enums";
import {FloatingLabel} from "components/src/common/FloatingLabel";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {ReplySizeUtils} from "components/src/utils/ReplySizeUtils";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import {Attachment as AttachmentIcon, SendDiagonal, Trash as DeleteIcon} from "iconoir-react";
import {useEffect, useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Spinner from "react-bootstrap/Spinner";

/*
 * REST api for accessing the back-end
 */
const webmailApi = new WebmailApi(ApiConfig.config());
const portalApi = new PortalApi(ApiConfig.config());

/**
 * The OnClose event should be handled by the owner. The owner is responsible for closing the dialog.
 */
type OnClose = (closeCause : DialogCloseCause) => void;

interface CreateNewMailProps {
    onClose : OnClose
}

export const CreateNewMailPopup = ({onClose} : CreateNewMailProps) =>
{
    /**
     * Subject of the email
     */
    const [subject, setSubject] = useState<string>();
    /**
     * Body of the email
     */
    const [body, setBody] = useState<string>();
    /**
     * Recipients of the email
     */
    const [recipients, setRecipients] = useState<string[]>([]);
    /**
     * Attachments
     */
    const [attachments, setAttachments] = useState<File[]>([]);
    /*
     * Max size in bytes of all attachments
     */
    const [attachmentsMaxSize, setAttachmentsMaxSize] = useState<number | undefined>();
    /*
     * Max number of attachments, undefined if there is none
     */
    const [attachmentsMaxCount, setAttachmentsMaxCount] = useState<number | undefined>();
    /*
     * Max size of the body, in characters
     */
    const [bodyMaxSize, setBodyMaxSize] = useState<number | undefined>();
    /**
     * The error message if some error occurred, which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {spinnerStateCounter, setSpinnerStateCounter} = useSpinnerStatusContext();
    /**
     * Set to true while sending to show a spinner on the download button
     */
    const [sending, setSending] = useState<boolean>(false);
    /**
     * Set to true when the user has made any changes to the form
     */
    const [isDirty, setIsDirty] = useState<boolean>(false);

    /**
     * The message was sent but the copy could not be filed in Sent Items. The message is gone either way,
     * so the dialog is not closed with an error: it says what happened and can only be closed, because
     * sending again would deliver the message twice
     */
    const [notSavedToSentItems, setNotSavedToSentItems] = useState<boolean>(false);
    /**
     * Set to true to show the discard-changes confirmation dialog
     */
    const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false);
    /**
     * Reference to the element which should receive focus on show
     */
    const focusRef = useRef(null as HTMLInputElement | null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    useEffect(() =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        portalApi.portalControllerGetPortalProperties().then(
            (response) => {
                setAttachmentsMaxSize(response.data.replyAttachmentsMaxSize)
                setAttachmentsMaxCount(response.data.replyAttachmentsMaxCount)
                setBodyMaxSize(response.data.replyBodyMaxSize)
            }
        )
        .finally(() => {
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setSpinnerStateCounter]);

    const sendMail = () =>
    {
        // sanity checks
        if (subject === undefined || subject.length === 0 || recipients.length === 0 || body === undefined
            || body.length === 0) {
            return;
        }

        // refuse a body which is too large before the mail is uploaded, instead of after
        const bodySizeError = ReplySizeUtils.getBodySizeError(body, bodyMaxSize, 'backend.webmail.body-too-large', t);

        if (bodySizeError !== undefined)
        {
            setErrorMessage(bodySizeError);
            return;
        }

        // reset error message
        setErrorMessage(undefined);

        setSending(true);

        webmailApi.webmailPortalControllerSendMail(subject, recipients, body, attachments).then(response => {
            if (response.data.savedToSentItems) {
                onClose(DialogCloseCause.Succes);
            }
            else {
                // sent, so nothing here may lead to sending it again
                setIsDirty(false);
                setNotSavedToSentItems(true);
            }
        })
        .catch((err: Error | AxiosError) => {
            setErrorMessage(Utils.getErrorMessage(err, t));
        })
        .finally(() => {
            setSending(false);
        });
    }

    function addAttachments(fileList : FileList)
    {
        const newFiles = Array.from(fileList);

        const attachmentsCheckError = ReplySizeUtils.getAttachmentsError(attachments, newFiles, attachmentsMaxSize,
            attachmentsMaxCount, t);

        if (attachmentsCheckError !== undefined)
        {
            setErrorMessage(attachmentsCheckError);
            return;
        }

        // reset error message
        setErrorMessage(undefined);

        setIsDirty(true);
        setAttachments(prevAttachments => [...prevAttachments, ...newFiles]);
    }

    function removeAttachment(file : File){
        setAttachments(prevAttachments => prevAttachments.filter(f => f !== file));
    }

    // called when the dialog is closed without sending the mail
    const onHideDialog = () => {
        if (isDirty) {
            setShowDiscardConfirm(true);
        } else {
            onClose(DialogCloseCause.Cancel);
        }
    }

    return (
        <>
        <Modal show={true}
               onHide={() => onHideDialog()}
               backdrop="static"
               scrollable={true}
               dialogClassName={"cm-modal-dialog"}
               contentClassName={"cm-modal-content"}
               onEntered={() => {focusRef.current?.focus()}}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {t('portal.messages.mail.new-mail-title')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column">
                <div className="row flex-grow-1">
                    <main className="col d-flex flex-column overflow-auto modal-main-body">
                        <RenderAlert message={errorMessage} setMessage={setErrorMessage}
                                     tooltipPlacement={"bottom"}/>

                        <RenderAlert message={notSavedToSentItems ?
                                         t('portal.messages.mail.sent-but-not-saved-to-sent-items') : undefined}
                                     variant="warning"
                                     tooltipPlacement={"bottom"}/>

                        <div className="d-flex flex-column flex-grow-1">
                            <FloatingLabel label={t('portal.messages.to')} className="text-secondary mb-3">
                                <Form.Control
                                    type="email"
                                    value={recipients.join(',')}
                                    placeholder={t('portal.messages.to')}
                                    multiple={true}
                                    onChange={e => { setIsDirty(true); setRecipients(e.target.value.split(',').map(s => s.trim())); }}
                                    ref={focusRef}
                                />
                            </FloatingLabel>

                            <FloatingLabel label={t('portal.messages.subject')} className="text-secondary mb-3">
                                <Form.Control
                                    type="text"
                                    value={subject ?? ""}
                                    onChange={e => { setIsDirty(true); setSubject(e.target.value); }}
                                    placeholder={t('portal.messages.subject')}
                                />
                            </FloatingLabel>

                            <Form.Group className="mb-3 d-flex flex-column flex-grow-1">
                                <Form.Control
                                    className="cm-mail-textarea flex-grow-1"
                                    as="textarea"
                                    aria-label={t('portal.messages.body')}
                                    onChange={e => { setIsDirty(true); setBody(e.target.value); }}
                                    value={body}
                                />
                            </Form.Group>

                            <Attachments
                                attachments={attachments}
                                dropdownItemText={t('portal.messages.mail.remove-attachment-title')}
                                onDropdownItemClick={(index) => removeAttachment(attachments[index])}
                                icon={<DeleteIcon className="me-1"/>}
                            />
                        </div>
                    </main>
                </div>
            </Modal.Body>
            <Modal.Footer className="justify-content-between">
                <div>
                    {/*
                     * The native file input is hidden (d-none) because it cannot be
                     * styled consistently across browsers. The Button below acts as
                     * the visible trigger and programmatically opens the file picker
                     * via fileInputRef.current?.click().
                     */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple={true}
                        accept=""
                        className="d-none"
                        onChange={e => {
                            addAttachments((e.target.files as FileList));
                            // otherwise picking the same file again, after it was refused or removed, does nothing
                            e.target.value = "";
                        }}
                    />
                    <Button variant="outline-light" className="text-decoration-none"
                            onClick={() => fileInputRef.current?.click()}>
                        <AttachmentIcon/> {t('portal.messages.mail.add-attachment-label')}
                    </Button>
                </div>
                <div className="d-flex gap-2">
                    <Button variant="outline-secondary"
                            className="d-none d-sm-inline"
                            onClick={onHideDialog}>
                        {notSavedToSentItems ? t('common.close') : t('portal.messages.mail.cancel')}
                    </Button>
                    <Button variant="success"
                            onClick={sendMail}
                            disabled={notSavedToSentItems || StringUtils.isBlank(body) || recipients.length === 0 || subject === undefined || subject.length === 0 || spinnerStateCounter > 0}>
                        <SendDiagonal className="me-2"/>
                        {sending && <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            aria-hidden="true"
                            className="me-1"
                        />}
                        {sending ? t('portal.messages.mail.sending') : t('portal.messages.mail.send')}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>

        <ConfirmDialog
            title={t('common.confirm')}
            bodyText={t('portal.messages.mail.discard-unsaved-warning')}
            confirmButtonTitle={t('portal.messages.mail.discard-unsaved-confirm')}
            onClickConfirmEvent={() => onClose(DialogCloseCause.Cancel)}
            showConfirm={showDiscardConfirm}
            setShowConfirm={setShowDiscardConfirm}
            buttonVariant="warning"
        />
        </>
    )
}